/*
(C) 2019, 2020, 2021, 2022, 2023, 2024 EDISOFT


System : ESA Datalabs

File Name : sepp_ui/src/global/api.ts

Author : EDISOFT, CGI, ACRI-ST

Creation Date : 2019-12-10

This file is subject to the terms and conditions defined in 'LICENSE.txt',
which is part of this source code package.
No part of the package, including this file, may be copied, modified, propagated,
or distributed except according to the terms contained in 'LICENSE.txt'.
*/

import { DatalabLogs, GraphqlApiBase, Logger } from "@datalabs/edl_common_ui";
import {
  Bookmark,
  QuotaGQ,
  RuntimeConfigurationGQ,
  WSMessage,
  DeploymentErrorGQ
} from "../interfaces";
import { PipelineRunUpdateMsg } from "./pipeline.state";
import { exponentialBackoff } from "./utils";

const logger = new Logger("api", "graphQL");

const GET_DATALAB_BUILDS = `query getBuilds($id: ID, $qs: String) {
  getBuilds(id: $id, qs: $qs) {
    version
  }
}`;

const SET_DATALAB_RUNTIME_CONFIGURATION = `mutation setDatalabBuildRuntimeData($build_id: ID!, $runtimeConfiguration: RuntimeConfigurationInput!, $clusterId: ID!){
  setDatalabBuildRuntimeData(
    build: $build_id
    runtime_configuration: $runtimeConfiguration
    cluster_id: $clusterId
  )
}`;

const ALL_PUBLISHED_LABS_BUILDS = `
query All($qs: String) {
    builds: getBuilds(qs: $qs) {
      id, datalabId, version,
      created_at, status
    }

    bookmarkedBuilds: getBookmarks {
      lab labBuild comment version
    }
}`;

const ALL_BOOKMARKED_LABS = `
query labPackagesAndLabBookmarks($qs: String) {
  builds: getBuilds(qs: $qs) {
    id, datalabId, version,
    created_at, status
  }
  bookmarkedBuilds: getBookmarks {
      id: labBuild, lab, comment, version
  }
}`;

const GET_BOOKMARKED = `query bookmarkedBuilds {
  getBookmarks {
      build: labBuild, datalab, comment, version
  }
}`;

const SEARCH_BUILDS = `query search_builds($qs: String) {
  builds: getBuilds(qs: $qs) {
    id, datalabId, version, status
  }
}`;



const GET_BUILDS_ID = `
query getBuildsID($qs: String) {
  buildsId: getBuilds(qs: $qs) {
    id, datalabId, name
  }
}`;

const GET_USER_QUOTA = `
query getQuota ($clusterId: ID){
  getQuota(cluster_id: $clusterId) {
    cpuLimit,memoryLimit,usedCpu,usedMemory,usedApp,appLimit,usedGroups,groupsLimit
  }
}`;

const datalabPackagesQuery = `
query datalabPackages($qs: String, $cat: Int, $clusterId: ID) {
  datalabPackages(qs: $qs, categories: $cat, cluster_id: $clusterId) {
    id, name, description, image, version
  }
}`;

const createDatavolMutation = `
mutation createDataVolume($input: DataVolumeInput!) {
  createDataVolume(input: $input) {dvId, name, path, url, dataCentreIds, clusterIds, automount}
}`;

const updateDatavolMutation = `
mutation updateDataVolume($dvId: ID!, $volumeInput: DataVolumeInput!) {
  updateDataVolume(dvId: $dvId, volumeInput: $volumeInput) {dvId, name, path, url, dataCentreIds, clusterIds, automount}
}`;

const destroyDatavolMutation = `
mutation destroyDataVolume($dvId: ID!) {
  destroyDataVolume(dvId: $dvId)
}`;

const mountDatavolMutation = `
mutation mountDataVolume($dvId: ID!, $dlId: ID!) {
  mountDataVolume(dvId: $dvId, dlId: $dlId)
}`;

const unmountDatavolMutation = `
mutation unmountDataVolume($resId: ID!) {
  unmountDataVolume(resId: $resId)
}`;

const GET_DEPLOYMENT_ERROR = `
query getDeploymentError($instance: ID!) {
  getDeploymentError(instance: $instance) {hasError, message}
}`;

function pipelineSubscriptionPayload(id: string) {
  const query = `subscription { monitorPipelineRun(id: \\"${id}\\") }`;
  return `{"id": "${id}","type":"start","payload":{"query":"${query}"}}`;
}

type DataVolumeInput = {
  url: string;
  path: string;
  name: string;
  dataCentreIds: string[];
  clusterIds: string[];
  automount: boolean;
};

export class Api extends GraphqlApiBase {
  backend: string = location.origin + "/graphql";
  websocketUrl: string = "ws" + this.backend.slice(4).replace("graphql", "ws");
  disconnectNotified: boolean = false;
  userEventRetryAttempt: number = 0;
  pingTimeout = null;
  monitorPipelineWs: WebSocket;
  monitorPipelineRetryAttempt: number = 0;
  subscriptions: { [id: string]: { start: string, process: (data: any) => void }} = {};
  websocketReconnectDelay = exponentialBackoff(0, 200, 5000);

  constructor() {
    super()
    this.createNewWebsocket.bind(this);
  }

  createNewWebsocket() {
    const ws = new WebSocket(this.websocketUrl);
    ws.onopen = () => {
      console.log(this.subscriptions);
      Object.keys(this.subscriptions).forEach((key) => {
        console.log(`Subscribing ${key}`);
        ws.send(this.subscriptions[key].start);
      });
    };
    ws.onopen.bind(this);
    ws.onmessage = (ev) => {
      const data = JSON.parse(ev.data);
      if (!data.payload || !data.payload.data) {
        if (data.type === "complete" && data.id) {
          // Subscription completed for id.
          delete this.subscriptions[data.id];
        } else {
          console.log("ignored: " + ev.data);
        }
        return;
      }
      this.subscriptions[data.id].process(data);
    };
    ws.onmessage.bind(this);
    ws.onclose = () => {
      const reconnectDelay = this.websocketReconnectDelay(this.monitorPipelineRetryAttempt);
      this.monitorPipelineRetryAttempt += 1;
      setTimeout(() => this.createNewWebsocket(), reconnectDelay);
    };
    ws.onclose.bind(this);
    this.monitorPipelineWs = ws;
  }

  subscribeToPipeline(id: string, callback: (runUpdate: PipelineRunUpdateMsg) => void) {
    this.subscriptions[id] = {
      process: (data) => {
        if (data.payload.data.monitorPipelineRun) {
          const runUpdate: PipelineRunUpdateMsg = JSON.parse(data.payload.data.monitorPipelineRun + "");
          callback(runUpdate);
        }
      },
      start: pipelineSubscriptionPayload(id),
    };

    if (!this.monitorPipelineWs) {
      this.createNewWebsocket();
    }
    const state = this.monitorPipelineWs.readyState;
    if (state === WebSocket.OPEN) {
      this.monitorPipelineWs.send(this.subscriptions[id]["start"]);
    }
  }

  createWebsocket(id: string, query: string, variables: any) {
    const webSocket = new WebSocket(this.websocketUrl);
    webSocket.onopen = () => {
      const payload: WSMessage = {
        id,
        type: "start",
        payload: {
          query,
          variables,
        },
      };
      webSocket.send(JSON.stringify(payload));
    };
    return webSocket;
  }

  subscribeToChannel(channelId: string) {
    return this.createWebsocket(channelId,
      "subscription monitorMessages($channelId: String!) {monitorMessages(channel: $channelId){type,body}}",
      { channelId }
    );
  }

  subscribeToUserEvents(userId: string) {
    const userEventWs = new WebSocket(this.websocketUrl);
    const query = "subscription { userEvent }";
    const resetPing = () => {
      if (this.pingTimeout) {
        clearTimeout(this.pingTimeout);
      }
      this.pingTimeout = setTimeout(() => {
        this.pingTimeout = null;
        resetPing();
        userEventWs.send('{"type":"ping"}');
      }, 58000);
    };
    userEventWs.onopen = () => {
      this.userEventRetryAttempt = 0;
      if (this.disconnectNotified) {
        this.disconnectNotified = false;
        window.dispatchEvent(new CustomEvent("edlConnectionEvent", { detail: true }));
      }
      userEventWs.send(`{"type":"start", "id":"${userId}","payload":{"query":"${query}"}}`);
      // userEventWs.send(`{"type":"start","payload":{"query":"${query}"}}`);
      resetPing();
    };
    // keep trying to reconnect
    userEventWs.onclose = () => {
      if (!this.disconnectNotified) {
        this.disconnectNotified = true;
        window.dispatchEvent(new CustomEvent("edlConnectionEvent", { detail: false }));
      }
      const reconnectDelay = this.websocketReconnectDelay(this.userEventRetryAttempt);
      this.userEventRetryAttempt += 1;
      setTimeout(() => this.subscribeToUserEvents(userId), reconnectDelay);
    };
    userEventWs.onmessage = (ev) => {
      resetPing();
      const data = JSON.parse(ev.data);
      if (!data.payload || !data.payload.data) {
        if (data.type == "complete") {
          // server finished the connection (restart?)
          userEventWs.close(); // close and try to restart the subscription
        } else {
          if (!ev.data.endsWith('< ping >."}}')) {
            console.log("ignored: " + ev.data);
          }
        }
        return;
      }
      const userEvent = JSON.parse(data.payload.data.userEvent);
      if (userEvent.type != "ping") {
        logger.info("user event", userEvent);
      }
      window.dispatchEvent(new CustomEvent(userEvent.type || "userEvent", { detail: userEvent.data }));
    };
  }

  datalabPackagesQuery(qs: string, clusterId: string) {
    return this.call(datalabPackagesQuery, { qs: qs, cat: 0xff, clusterId: clusterId });
  }

  createDataVolume(input: DataVolumeInput) {
    this.call(createDatavolMutation, { input: input }).then((res) => {
      if (!res.data) {
        console.log("Something went wrong creating the data volume");
      } else {
        res.data.createDataVolume["id"] = res.data.createDataVolume["dvId"];
        window.dispatchEvent(new CustomEvent("datavolumeCreated", { detail: res.data.createDataVolume }));
      }
    });
  }

  updateDataVolume(dvId: string, volumeInput: DataVolumeInput) {
    this.call(updateDatavolMutation, { dvId: dvId, volumeInput: volumeInput }).then((res) => {
      if (!res.data) {
        console.log("Something went wrong updating the data volume ", dvId);
      } else {
        res.data.updateDataVolume["id"] = res.data.updateDataVolume["dvId"];
        window.dispatchEvent(new CustomEvent("datavolumeUpdated", { detail: res.data.updateDataVolume }));
      }
    });
  }

  destroyDataVolume(dvId: string) {
    this.call(destroyDatavolMutation, { dvId: dvId }).then((res) => {
      if (!res.data) {
        console.log("Something went wrong destroying the data volume ", dvId);
      } else {
        window.dispatchEvent(new CustomEvent("datavolumeDestroyed", { detail: dvId }));
      }
    });
  }

  mountDataVolume(dvId: string, dlId: string) {
    this.call(mountDatavolMutation, { dvId: dvId, dlId: dlId }).then((res) => {
      if (!res.data) {
        console.log("Something went wrong mounting a data volume!");
      } else {
        console.log("On mount data volume response: ", res.data);
        console.log(res);
      }
    });
  }

  unmountDataVolume(resId: string) {
    this.call(unmountDatavolMutation, { resId: resId }).then((res) => {
      if (!res.data) {
        console.log("Something went wrong unmounting a data volume!");
      } else {
        console.log("On unmount data volume response: ", res.data);
        console.log(res);
      }
    });
  }

  async getBuilds(id) {
    return this.call(GET_DATALAB_BUILDS, { id }).then(({ data }) => {
      return data;
    });
  }

  async setDatalabBuildRuntimeData(build_id: string, runtimeConfiguration: RuntimeConfigurationGQ, clusterId: string): Promise<boolean> {
    return this.call(SET_DATALAB_RUNTIME_CONFIGURATION, {
      build_id,
      runtimeConfiguration,
      clusterId,
    }).then(({ data }) => {
      return data;
    });
  }

  async allPublishedLabsBuilds(qs) {
    return this.call(ALL_PUBLISHED_LABS_BUILDS, { qs }).then(({ data }) => {
      return data;
    });
  }

  async allBookmarkLabs(qs) {
    return this.call(ALL_BOOKMARKED_LABS, { qs }).then(({ data }) => {
      return data;
    });
  }

  async getBookmarks(): Promise<Bookmark[]> {
    return this.call(GET_BOOKMARKED, {}).then(({ data }) => {
      return data.getBookmarks;
    });
  }


  async searchBuilds(search?: string) {
    return this.call(SEARCH_BUILDS, { qs: search }).then(({ data }) => {
      return data;
    });
  }

  async getBuildsID(qs: string) {
    return this.call(GET_BUILDS_ID, { qs }).then(({ data }) => {
      return data;
    });
  }

  async getUserQuota(clusterId: string): Promise<QuotaGQ> {
    return this.call(GET_USER_QUOTA, { clusterId: clusterId }).then(
      ({ data }: { data: { getQuota: QuotaGQ } }) => {
        return data.getQuota;
      }
    );
  }

  async getDeploymentError(instanceId: string): Promise<DeploymentErrorGQ> {
    return this.call(GET_DEPLOYMENT_ERROR, {
        instance: instanceId
      }).then(({ data }: { data: { getDeploymentError: DeploymentErrorGQ } }) => {
      return data.getDeploymentError;
    });
  }

  async mountVolume(datalabId: string, volumeId: string): Promise<boolean> {
    return this.call(
      `
    mutation mountDatalabVolume($id: ID! $volume: ID!) {
      mountDatalabVolume(instanceId: $id, dvId: $volume)
    }
    `,
      {
        id: datalabId,
        volume: volumeId,
      }
    ).then(({ data }: { data: { mountDatalabVolume: boolean } }) => {
      return data.mountDatalabVolume;
    });
  }

  async getPodLog(instanceId: string): Promise<DatalabLogs> {
    const clusterId = localStorage.getItem("clusterId");
    return api.call(`query { logs(id:"${instanceId}", clusterId:"${clusterId}"){logs,params{pod}} }`).then((res) => {
      return res.data.logs;
    });
  }
}

const api = new Api();
// for backward compatibility
export { api };
// for mocking in tests
export default Api;
